import type { MouseEvent } from 'react'
import { Bookmark } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

export type JobBoardSaveButtonProps = {
  saved: boolean
  disabled?: boolean
  onClick: (event: MouseEvent<HTMLButtonElement>) => void
  /** `lg` is a larger bookmark for public company job cards. Default matches job-board lists. */
  size?: 'sm' | 'lg'
}

const iconSwapClassName =
  'transition-[opacity,filter,scale] duration-300 [transition-timing-function:cubic-bezier(0.2,0,0,1)] motion-reduce:transition-none'

/** Icon-only secondary save control for job-board cards and candidate job detail header. */
export function JobBoardSaveButton({
  saved,
  disabled = false,
  onClick,
  size = 'sm',
}: JobBoardSaveButtonProps) {
  const large = size === 'lg'
  const iconSizeClass = large ? 'size-5' : 'h-4 w-4'

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghostToolbar"
          size="iconSm"
          pressFeedback
          className={cn(
            'relative after:absolute after:left-1/2 after:top-1/2 after:size-10 after:-translate-x-1/2 after:-translate-y-1/2 after:content-[""]',
            large && '[&_svg]:size-5',
          )}
          disabled={disabled}
          aria-label={saved ? 'Saved' : 'Save'}
          aria-pressed={saved}
          onClick={onClick}
        >
          <span className={cn('relative', large ? 'size-5' : 'size-4')}>
            <Bookmark
              className={cn(
                'absolute inset-0',
                iconSizeClass,
                iconSwapClassName,
                saved
                  ? 'scale-100 opacity-100 blur-0 fill-current motion-reduce:scale-100 motion-reduce:opacity-100 motion-reduce:blur-0'
                  : 'scale-[0.25] opacity-0 blur-[4px] motion-reduce:scale-100 motion-reduce:opacity-0 motion-reduce:blur-0',
              )}
              aria-hidden
            />
            <Bookmark
              className={cn(
                iconSizeClass,
                iconSwapClassName,
                saved ? 'scale-[0.25] opacity-0 blur-[4px]' : 'scale-100 opacity-100 blur-0',
              )}
              aria-hidden
            />
          </span>
        </Button>
      </TooltipTrigger>
      <TooltipContent>{saved ? 'Saved' : 'Save'}</TooltipContent>
    </Tooltip>
  )
}
